import { createApi } from '@reduxjs/toolkit/query/react';
import { defaultIChannelKpiThresholds, defaultIQueueKpiThresholds, defaultITeamKpiThresholds, IChannelKpiThresholds, IQueueKpiThresholds, ITeamKpiThresholds } from '../../models/reports.thresholds';
import { cache404ResponseQueryFn, createRbacBaseQuery, post } from '../../utils/helpers';

const reportingThresholdsApi = createApi({
    reducerPath: 'reportingThresholds',
    tagTypes: ['Timezone', 'QueueKpiThreshold', 'TeamKpiThreshold', 'ChannelKpiThreshold'],
    baseQuery: createRbacBaseQuery(''),
    endpoints: (builder) => ({
        getTimezoneId: builder.query<string, void>({
            query: () => '/timezone',
            providesTags: ['Timezone'],
            transformResponse: (result: { timeZoneId: string }) => result.timeZoneId,
        }),
        updateTimezoneId: builder.mutation<void, string>({
            query: (timeZoneId) => post('/timezone', { timeZoneId }),
            invalidatesTags: ['Timezone'],
        }),
        getQueueKpiThresholds: builder.query<Partial<IQueueKpiThresholds>, string>({
            queryFn: cache404ResponseQueryFn({
                query: (queueId: string) => `/kpiThresholds/queues/${queueId}`,
                defaultValue: defaultIQueueKpiThresholds,
                transformResponse: (result: { thresholds: string | null } | undefined) => result?.thresholds ? JSON.parse(result.thresholds) : null,
            }),
            providesTags: (_, __, queueId) => [{ type: 'QueueKpiThreshold', id: queueId }],
        }),
        updateQueueKpiThresholds: builder.mutation<void, { queueId: string, thresholds: IQueueKpiThresholds }>({
            query: ({ queueId, thresholds }) => post(`/kpiThresholds/queues/${queueId}`, { thresholds: JSON.stringify(thresholds) }),
            invalidatesTags: (_, __, { queueId }) => [{ type: 'QueueKpiThreshold', id: queueId }],
        }),
        getTeamKpiThresholds: builder.query<Partial<ITeamKpiThresholds>, string>({
            queryFn: cache404ResponseQueryFn({
                query: (teamId: string) => `/kpiThresholds/teams/${teamId}`,
                defaultValue: defaultITeamKpiThresholds,
                transformResponse: (result: { thresholds: string | null } | undefined) => result?.thresholds ? JSON.parse(result.thresholds) : null,
            }),
            providesTags: (_, __, teamId) => [{ type: 'TeamKpiThreshold', id: teamId }],
        }),
        updateTeamKpiThresholds: builder.mutation<void, { teamId: string, thresholds: ITeamKpiThresholds }>({
            query: ({ teamId, thresholds }) => post(`/kpiThresholds/teams/${teamId}`, { thresholds: JSON.stringify(thresholds) }),
            invalidatesTags: (_, __, { teamId }) => [{ type: 'TeamKpiThreshold', id: teamId }],
        }),
        getChannelKpiThresholds: builder.query<Partial<IChannelKpiThresholds>, string>({
            queryFn: cache404ResponseQueryFn({
                query: (channelId: string) => `/kpiThresholds/channels/${channelId}`,
                defaultValue: defaultIChannelKpiThresholds,
                transformResponse: (result: { thresholds: string | null } | undefined) => result?.thresholds ? JSON.parse(result.thresholds) : null,
            }),
            providesTags: (_, __, channelId) => [{ type: 'ChannelKpiThreshold', id: channelId }],
        }),
        updateChannelKpiThresholds: builder.mutation<void, { channelId: string, thresholds: IChannelKpiThresholds }>({
            query: ({ channelId, thresholds }) => post(`/kpiThresholds/channels/${channelId}`, { thresholds: JSON.stringify(thresholds) }),
            invalidatesTags: (_, __, { channelId }) => [{ type: 'ChannelKpiThreshold', id: channelId }],
        }),
    }),
});

export default reportingThresholdsApi;

export const {
    useGetTimezoneIdQuery,
    useUpdateTimezoneIdMutation,
    useGetQueueKpiThresholdsQuery,
    useUpdateQueueKpiThresholdsMutation,
    useGetTeamKpiThresholdsQuery,
    useUpdateTeamKpiThresholdsMutation,
    useGetChannelKpiThresholdsQuery,
    useUpdateChannelKpiThresholdsMutation,
} = reportingThresholdsApi;